/* eslint-disable complexity */
import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";

import useCheckoutPageProps from "@hooks/useCheckoutPageProps";
import useSessionStateSync from "@hooks/useSessionStateSync";

import { useAtom, useSetAtom } from "jotai";

import { CheckoutPageRoutesEnum } from "@ts/enums/CheckoutPageRoutesEnum";
import { UserCheckoutStateInterface } from "@ts/interfaces/Checkout/UserCheckoutStateInterface";
import { CheckoutStateInterface } from "@ts/interfaces/Misc/state/CheckoutStateInterface";

import {
    checkoutAtom,
    isProvidedQueryParamsAtom,
    loadableProductFamiliesAtom,
    loadableUserCheckoutStateApiAtom,
    pageQueryParamsAtom,
    userCheckoutStateTokenAtom
} from "@atoms/checkoutAtom";

import { mapTokenDataToCheckoutAtom } from "@utils/checkoutDataMapper";

/**
 * This "useFetchCheckoutState" custom hook is expected to be used in "CheckoutLayout" component.
 * This hook mainly handles the data fetching logic and the redirection logic that is required by the
 * "CheckoutLayout" component to decide on which page in the checkout portal is shown.
 */
const useFetchCheckoutState = () => {
    const { parentUser, students } = useCheckoutPageProps();

    const isParentUserLoggedIn = !!parentUser;

    const matchStudentSelectPage = /^\/checkout\/select-student(\/+)?$/.test(
        window.location.pathname
    );

    const matchCreateNewStudentPage =
        /^\/checkout\/create-new-student(\/+)?$/.test(window.location.pathname);

    // const matchUserInfoPage = /^\/checkout(\/+)?$/.test(
    //     window.location.pathname
    // );

    const matchProductFamilySelectPage =
        /^\/checkout\/select-age-group(\/+)?$/.test(window.location.pathname);

    const matchPlanSelectPage = /^\/checkout\/select-membership(\/+)?$/.test(
        window.location.pathname
    );

    const matchAddonSelectPage =
        /^\/checkout\/select-premium-services(\/+)?$/.test(
            window.location.pathname
        );

    const matchPaymentPage = /^\/checkout\/payment(\/+)?$/.test(
        window.location.pathname
    );

    const redirect = {
        shouldRedirect: false,
        pathRedirectTo: CheckoutPageRoutesEnum.PRODUCT_FAMILY_SELECT_PAGE,
        search: ""
    };

    const [productFamilies] = useAtom(loadableProductFamiliesAtom);
    const [userCheckoutState] = useAtom(loadableUserCheckoutStateApiAtom);
    const [checkout] = useAtom(checkoutAtom);
    const [isProvidedQueryParams] = useAtom(isProvidedQueryParamsAtom);
    const setPageQueryParams = useSetAtom(pageQueryParamsAtom);

    const setCheckout = useSetAtom(checkoutAtom);
    const setUserCheckoutStateToken = useSetAtom(userCheckoutStateTokenAtom);

    const [searchParams] = useSearchParams();
    const token = searchParams.get("token");
    const productFamilyId = searchParams.get("family");
    const gradeName = searchParams.get("grade");
    const planId = searchParams.get("plan");
    const addonId = searchParams.get("addon");
    const coupon = searchParams.get("coupon");
    const locationState = searchParams.get("locationState");
    const origin = searchParams.get("origin");
    const skip = searchParams.get("skip");

    const [, sessionState] = useSessionStateSync<CheckoutStateInterface>();

    const checkoutPurchasingAddon =
        sessionState.checkout?.checkoutPurchasingAddon;

    /**
     * useEffect to fetch the current checkout state data from backend, if the "token" query parameter is set.
     */
    useEffect(() => {
        /**
         * If the product families already loaded and token parameter is set.
         */
        if (token && productFamilies.state === "hasData") {
            /**
             * Setting the token will trigger the network request to fetch the current checkout state data, based on the token we passed in.
             * "userCheckoutState" object keeps the state and the response data of that request.
             * Initial state of the "userCheckoutState" object is set to "loading", right after this component being rendered for the first time.
             */
            setUserCheckoutStateToken(token);
        }
    }, [token, productFamilies]);

    /**
     * useEffect to update the "checkoutAtom", if the passed token brings us the current checkout data recorded against it.
     */
    useEffect(() => {
        /**
         * If the token we passed in was valid, we could access the current checkout data.
         */
        if (
            userCheckoutState.state === "hasData" &&
            productFamilies.state === "hasData"
        ) {
            const { data } = userCheckoutState.data;
            setUserCheckoutStateToken(data.token);

            /**
             * We have the current checkout state data. We now have to constrcut the checkout session atom based on this data.
             * Updating this "checkoutAtom" will also update the "userCheckoutStateAtom" consuming the data of the "checkoutAtom".
             * We need this "userCheckoutStateAtom" to keep track of the product selections and other state data.
             * "userCheckoutStateAtom" is being used to send the current checkout state to the backend, each time user navigates to the next page.
             */
            setCheckout(
                mapTokenDataToCheckoutAtom(
                    JSON.parse(data.token_data),
                    productFamilies.data,
                    students
                )
            );
        }

        /**
         * The passed token is invalid, set the "userCheckoutStateTokenAtom" back to null state.
         */
        if (userCheckoutState.state === "hasError") {
            setUserCheckoutStateToken(null);
        }
    }, [userCheckoutState, productFamilies]);

    /**
     * Handling the flow when a token (from the query parameter) is not provided or the provided token is invalid.
     * In this flow, users shouldn't be allowed to access a page that requires a pre-selection of a product.
     */
    if (
        (!token || userCheckoutState.state === "hasError") &&
        productFamilies.state === "hasData"
    ) {
        /**
         * We firstly check if we can see query parameters are set in the URL.
         * This means that based on the query parameters, we need to construct the next route to be redirected to
         * and also the checkout state.
         *
         * We come here evenif the user refreshes the page when the query parameters are still present (unless we don't provide any condition).
         */
        if (
            ((productFamilyId && gradeName) ||
                planId ||
                addonId ||
                coupon ||
                origin) &&
            // Check to see whether these query parameters provided from an external source directly into the URL
            isProvidedQueryParams &&
            // Check to see whether checkout is accessed with an active parent user session
            // !isParentUserLoggedIn
            /**
             * If user is trying to add only a premium service (hence comming from parent portal)
             * we don't need to consider for this logic.
             */
            !sessionState.checkout?.checkoutPurchasingAddon
        ) {
            redirect.shouldRedirect = true;
            redirect.pathRedirectTo = CheckoutPageRoutesEnum.USER_INFO_PAGE;
            /**
             * Update "pageQueryParams" atoms with the query parameters that we captured at this point.
             * This will be consumed by the "UserInfoContainer" container.
             *
             * NOTE: We are setting the page query parameters at this point here is because
             * the next redirection URL will be replaced with /checkout.
             * So we need a way of storing the received query parameters.
             */
            setPageQueryParams({
                family: productFamilyId,
                grade: gradeName,
                plan: planId,
                addon: addonId,
                coupon,
                locationState,
                skip,
                origin
            });
        } else {
            /**
             * We can't see query parameters set in the URL.
             * This means that we need to handle any route separately, and allow only routes if checkout data is available
             * for those pages require in order to display.
             */
            const isUserInfoPageCompleted =
                !!checkout.user.email && !!checkout.user.firstName;

            // const queryParamsPresent = !!(
            //     (productFamilyId && gradeName) ||
            //     planId ||
            //     addonId ||
            //     coupon ||
            //     origin
            // );

            if (matchProductFamilySelectPage && !isUserInfoPageCompleted) {
                redirect.shouldRedirect = true;
                redirect.pathRedirectTo = CheckoutPageRoutesEnum.USER_INFO_PAGE;
            }

            /**
             * NOTE: In all of the below conditions we also want to check, whether a parent user is logged in,
             * if so, do a redirection to product family select page
             * (Even if user info page is completed, there can be a parent user logged in)
             */

            // if (
            //     matchUserInfoPage &&
            //     isParentUserLoggedIn &&
            //     !queryParamsPresent
            // ) {
            //     redirect.shouldRedirect = true;
            // }
            if (
                matchPlanSelectPage &&
                (!isUserInfoPageCompleted || isParentUserLoggedIn) &&
                !checkout.productFamily &&
                !checkout.grade
            ) {
                redirect.shouldRedirect = true;
            }
            if (
                matchAddonSelectPage &&
                /**
                 * Parent user is comming from parents portal, trying to purchase an addon
                 * so don't do a redirection
                 */
                !checkoutPurchasingAddon &&
                (!isUserInfoPageCompleted || isParentUserLoggedIn) &&
                !checkout.productFamily &&
                !checkout.grade &&
                !checkout.plan
            ) {
                redirect.shouldRedirect = true;
            }
            // Not checking for the addon, even if they are available to select from under the selected plan
            if (
                matchPaymentPage &&
                (!isUserInfoPageCompleted || isParentUserLoggedIn) &&
                !checkout.productFamily &&
                !checkout.grade &&
                !checkout.plan
            ) {
                redirect.shouldRedirect = true;
            }

            /**
             * Checking whether the user trying to navigate to student selection page directly,
             * so not allowing them to navigate unless they are coming from ecom
             * (should be logged in)
             */
            if (
                matchStudentSelectPage &&
                (!isUserInfoPageCompleted || isParentUserLoggedIn) &&
                origin !== "ecommerce"
            ) {
                redirect.shouldRedirect = true;
            }
            /**
             * Checking whether the user trying to navigate to create student page directly,
             * so not allowing them to navigate unless they are coming from ecom
             * (should be logged in)
             */
            if (
                matchCreateNewStudentPage &&
                (!isUserInfoPageCompleted || isParentUserLoggedIn) &&
                origin !== "ecommerce"
            ) {
                redirect.shouldRedirect = true;
            }
        }
    }

    return {
        // We are allowed to show any content in the checkout now.
        canShowContent:
            (!token || userCheckoutState.state === "hasError") &&
            productFamilies.state === "hasData",
        // Flow when a token (from the query parameter) is not provided or the provided token is invalid.
        redirect,
        /**
         * Flow when a valid token is provided, and "checkoutAtom" is updated with the checkout data state.
         * In this flow, query parameter values are not considered.
         */
        canNavigateTo: userCheckoutState.state === "hasData",
        pathNavigateTo:
            userCheckoutState.state === "hasData"
                ? (
                      JSON.parse(
                          userCheckoutState.data.data.token_data
                      ) as UserCheckoutStateInterface
                  ).nextAttemptPage
                : null
    };
};

export default useFetchCheckoutState;
